import React, { useCallback, useEffect, useState } from 'react';
import { getCookie, setCookie, getCurrentLocale } from '../../../common/utils/cookies';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { redirectUrlOnAuthentication } from '../../../common/utils/redirectUrlOnAuthentication';
import { reValidateSession } from '../../utils/revalidateSession';
import { Button, Grid, InputLabel, Typography, Select, MenuItem, List, ListItem } from '@material-ui/core';
import axios from 'axios';
import NavigateNextIcon from '@material-ui/icons/NavigateNext';
import { apiUrls, SessionStore } from '../../../constants';
import { AccountSelectionPageStyles } from '../../styles/accountSelectionPage';
import { FullPageLoader } from '../../../common/components';
import { Account } from '../../interfaces/Account';
import { Language } from '../../interfaces/Language';
import { RaterStyles } from '../../../survey/styles/rater';
import { RatingQuestionStyles } from '../../../survey/styles';
import { QuestionTheme } from '../../../survey/styles/mfsTheme';
import { getAccountDisplayName } from '../../utils/account';
import { isSameLanguage } from '../../utils/language';
import { LanguageDropdownStyles } from '../../../common/styles/languageDropdown';
import { NeutralDivider } from '../../../common/components/NeutralDivider';
import { useCurrentAccount } from '../../../survey/hooks/useCurrentAccount';
import { useAppSelector } from '../../../redux-store/hook';

const languageCache = new Map();

export const MfsAccountSelectionPage = (): JSX.Element => {
  const template = useAppSelector((state) => state.template);
  const candidate = useAppSelector((state) => state.candidate);
  const { settings } = template;
  const { t } = useTranslation();
  const history = useHistory();
  const classes = AccountSelectionPageStyles(settings);
  const clsR = RaterStyles();
  const clsRQ = RatingQuestionStyles(QuestionTheme);
  const classedLanguageDropdown = LanguageDropdownStyles(settings);
  const accountIdFromSessionStorage = Number(sessionStorage.getItem(SessionStore.selectedAccountId)) || 0;
  const account = useCurrentAccount(!!accountIdFromSessionStorage);

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [languages, setLanguages] = useState<Language[]>([]);
  const [selectedLanguage, setSelectedLanguage] = useState<string>(getCurrentLocale());
  const [selectedAccount, setSelectedAccount] = useState<number>(accountIdFromSessionStorage || account?.id || 0);

  const revalidateSessionAndNavigateAfterAuthentication = useCallback(async (): Promise<void> => {
    try {
      /** revalidate session and navigate to current workflow item */
      const { data: nextWfItem, error } = await reValidateSession(template);

      if (nextWfItem && !error) {
        const url = await redirectUrlOnAuthentication(nextWfItem, { template, candidate });
        if (url) {
          history.push(url);
        }
      } else {
        setErrorMessage(t('genericError'));
      }
    } catch (error) {
      setErrorMessage(t('genericError'));
    }
  }, [history, t]);

  const onContinueClick = async (): Promise<void> => {
    try {
      const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
      await axios.post(apiUrls.POST_CHANGE_ACCOUNT.replace('{accountId}', `${selectedAccount}`), null, config);

      revalidateSessionAndNavigateAfterAuthentication();

      sessionStorage.removeItem('selectedAccountId');
    } catch (error) {
      setErrorMessage(t('genericError'));
    }
  };

  const fetchLanguages = useCallback(async (): Promise<void> => {
    if (!selectedAccount) {
      return;
    }

    try {
      let languages: Language[];

      if (languageCache.has(selectedAccount)) {
        languages = languageCache.get(selectedAccount);
      } else {
        ({ data: languages } = await axios.get(
          apiUrls.GET_ACCOUNT_LANGUAGES.replace('{accountId}', `${selectedAccount}`),
        ));
        languageCache.set(selectedAccount, languages);
      }

      setLanguages(languages);
      sessionStorage.setItem(SessionStore.mfsLanguages, JSON.stringify(languages));
    } catch (error) {
      setErrorMessage(t('genericError'));
    }
  }, [selectedAccount, t]);

  const changeLanguage = useCallback(
    async (locale: string): Promise<void> => {
      try {
        if (isSameLanguage(selectedLanguage, locale)) {
          return;
        }

        const config = { headers: { sid: getCookie('sid') }, withCredentials: false };
        await axios.post(apiUrls.POST_SESSION_LANGUAGE, { language: locale }, config);

        setSelectedLanguage(locale);
        setCookie('locale_player', locale);
        setCookie('oldlang', locale);
        await reValidateSession(template, 'fetchTemplate=true');
        window.location.reload();
      } catch {
        setErrorMessage(t('genericError'));
      } finally {
        setLoading(false);
      }
    },
    [selectedLanguage, t],
  );

  useEffect(() => {
    if (account) {
      setSelectedAccount(account.id);
    }
  }, [account]);

  useEffect(() => {
    if (selectedAccount) {
      sessionStorage.setItem(SessionStore.selectedAccountId, `${selectedAccount}`);
    }
  }, [selectedAccount]);

  useEffect(() => {
    if (languages?.length > 0) {
      const selectedLanguageFoundInAccountLanguages = languages.some((language: Language) =>
        isSameLanguage(language.locale, selectedLanguage),
      );

      if (!selectedLanguageFoundInAccountLanguages) {
        const defaultLanguageId = accounts.find((account) => account.id === selectedAccount)?.defaultUserLanguageId;
        const locale = languages.find((language) => language.languageID === defaultLanguageId)?.locale || 'en_US';

        changeLanguage(locale);
      }
    }
  }, [accounts, changeLanguage, languages, selectedAccount, selectedLanguage]);

  useEffect(() => {
    const fetchAccounts = async (): Promise<void> => {
      try {
        const { data } = await axios.get(apiUrls.GET_LINKED_ACCOUNTS);

        if (data.length === 1) {
          revalidateSessionAndNavigateAfterAuthentication();
        } else {
          setAccounts(data);
          setLoading(false);
        }
      } catch (error) {
        setErrorMessage(t('genericError'));
      }
    };

    fetchAccounts();
  }, [revalidateSessionAndNavigateAfterAuthentication, t]);

  useEffect(() => {
    fetchLanguages();
  }, [fetchLanguages]);

  const onAccountSelectionClick = (value: number): void => {
    setSelectedAccount(value);
  };

  const onLanguageChange = (event: React.ChangeEvent<{ name?: string; value: unknown }>): void => {
    setLoading(true);

    changeLanguage(event.target.value as string);
  };

  /**
   * this is a bad practice hack
   * all languages should come with the same locale form and pattern
   * Thai language locale needs to be changed on DB from TH_TH to th_TH
   */
  const formatLocale = (locale: string): string => {
    const segments = locale.split('_');
    return `${segments[0].toLowerCase()}_${segments[1].toUpperCase()}`;
  };

  return (
    <>
      <FullPageLoader isLoading={loading && errorMessage === ''} />

      {!loading && (
        <Grid className={classes.mainContainer} container justify="center" alignItems="center">
          <Grid className={classes.container} container justify="center" alignItems="center" direction="column">
            <Grid item className={classes.grid}>
              <Typography className={classes.title} align="center">
                {t('mfsAccSelectionTitle')}
              </Typography>
            </Grid>
            <Grid item>
              <Typography className={classes.subtitle} align="center">
                {t('mfsAccSelectionSubtitle')}
              </Typography>
            </Grid>
            <Grid item container className={classes.grid}>
              <List tabIndex={0} className={`${classes.grid} ${classes.list}`} disablePadding>
                {accounts.map((account: Account) => {
                  return (
                    <ListItem
                      key={account.id}
                      button
                      selected={selectedAccount === account.id}
                      classes={{
                        root: classes.item,
                        selected: classes.selectedItem,
                      }}
                      onClick={(): void => onAccountSelectionClick(account.id)}>
                      {getAccountDisplayName(account)}
                    </ListItem>
                  );
                })}
              </List>
            </Grid>
            <Grid item container direction="row" key="language-dropdown" className={classes.footer}>
              <NeutralDivider className={classes.divider} />
              <Grid item container sm={6} direction="row" alignItems="center" justify="space-between">
                <InputLabel id="language">{t('language')}</InputLabel>
                <Select
                  aria-label="Languages List"
                  classes={{ icon: classes.selectIcon, select: classes.selectSelect }}
                  className={classes.select}
                  disabled={languages?.length === 0}
                  disableUnderline
                  displayEmpty
                  MenuProps={{
                    classes: {
                      list: `${classedLanguageDropdown.languageList} ${classes.menuList}`,
                    },
                    disablePortal: true,
                  }}
                  onChange={onLanguageChange}
                  tabIndex={0}
                  value={languages && languages.length > 0 ? formatLocale(selectedLanguage) : ''}
                  variant="standard">
                  {(!languages || languages.length === 0) && <MenuItem value="">{t('select')}</MenuItem>}
                  {languages &&
                    languages.map((language: Language) => {
                      return (
                        <MenuItem
                          key={formatLocale(language.locale)}
                          value={formatLocale(language.locale)}
                          selected={isSameLanguage(selectedLanguage, language.locale)}>
                          {language.languageName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </Grid>
              <Grid item container sm={6} alignItems="center" justify="flex-end">
                <Button
                  variant="outlined"
                  color="primary"
                  className={`${clsR.doneButton} ${classes.continueButton}`}
                  aria-label={t('continue')}
                  disabled={!selectedAccount}
                  onClick={onContinueClick}>
                  {t('continue')} <NavigateNextIcon className={clsRQ.nextArrowIcon} />
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      )}
    </>
  );
};
